import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/BloggerDashboard.css';
import {
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaThumbsUp,
  FaComments,
  FaEye,
  FaPlus,
  FaArrowRight,
  FaEdit,
  FaTrash,
  FaShare
} from 'react-icons/fa';

import BloggerSidebar from '../components/Blogger/BloggerSidebar';
const BloggerDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBlogs: 0,
    publishedBlogs: 0,
    draftBlogs: 0,
    totalLikes: 0,
    totalComments: 0,
    totalViews: 0
  });
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/blogs/my-blogs?limit=6`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        const blogs = response.data.data;
        
        // Calculate statistics
        const published = blogs.filter(b => b.status === 'published');
        const drafts = blogs.filter(b => b.status === 'draft');
        const totalLikes = blogs.reduce((sum, b) => sum + (b.likes || 0), 0);
        const totalComments = blogs.reduce((sum, b) => sum + (b.comments?.length || 0), 0);
        const totalViews = blogs.reduce((sum, b) => sum + (b.views || 0), 0);

        setStats({
          totalBlogs: response.data.pagination?.totalBlogs || blogs.length,
          publishedBlogs: published.length,
          draftBlogs: drafts.length,
          totalLikes,
          totalComments,
          totalViews
        });

        setRecentBlogs(blogs.slice(0, 4));
        setError('');
      }
    } catch (err) {
   
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getContentPreview = (content, length = 80) => {
    const div = document.createElement('div');
    div.innerHTML = content;
    const text = div.textContent || div.innerText || '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  const handleDeleteBlog = async (blogId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      try {
        const token = sessionStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/api/blogs/${blogId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        // Refresh the dashboard data
        fetchDashboardData();
      } catch (err) {
     
        alert('Failed to delete blog');
      }
    }
  };

  const handleShareBlog = (blog, e) => {
    e.stopPropagation();
    const blogUrl = `${window.location.origin}/blogger/blogs/${blog._id}`;
    const text = `Check out this blog: ${blog.title}`;
    
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: text,
        url: blogUrl
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(blogUrl);
      alert('Blog link copied to clipboard!');
    }
  };

  return (
    <div className="blogger-dashboard-container">
      <BloggerSidebar />
      
      <div className="dashboard-wrapper-blogger">
        {/* Header Section */}
        <div className="dashboard-header">
          <div>
            <h1>Welcome to Your Dashboard</h1>
            <p>Manage and track your blog content</p>
          </div>
          <button 
            className="btn-create-new"
            onClick={() => navigate('/blogger/blogs/create')}
          >
            <FaPlus /> Create New Blog
          </button>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your dashboard...</p>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="stats-grid">
              <div className="stat-card total">
                <div className="stat-icon">
                  <FaFileAlt />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{stats.totalBlogs}</div>
                  <div className="stat-label">Total Blogs</div>
                </div>
              </div>

              <div className="stat-card published">
                <div className="stat-icon">
                  <FaCheckCircle />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{stats.publishedBlogs}</div>
                  <div className="stat-label">Published</div>
                </div>
              </div>

              <div className="stat-card draft">
                <div className="stat-icon">
                  <FaClock />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{stats.draftBlogs}</div>
                  <div className="stat-label">Drafts</div>
                </div>
              </div>

              <div className="stat-card engagement">
                <div className="stat-content-horizontal">
                  <div className="engagement-item">
                    <FaThumbsUp className="engagement-icon" />
                    <div>
                      <div className="stat-number">{stats.totalLikes}</div>
                      <div className="stat-label">Likes</div>
                    </div>
                  </div>
                  <div className="engagement-item">
                    <FaComments className="engagement-icon" />
                    <div>
                      <div className="stat-number">{stats.totalComments}</div>
                      <div className="stat-label">Comments</div>
                    </div>
                  </div>
                  <div className="engagement-item">
                    <FaEye className="engagement-icon" />
                    <div>
                      <div className="stat-number">{stats.totalViews}</div>
                      <div className="stat-label">Views</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Blogs Section */}
            <div className="recent-blogs-section">
              <div className="section-header">
                <h2>Recent Blogs</h2>
                <button 
                  className="view-all-link"
                  onClick={() => navigate('/blogger/blogs')}
                >
                  View All <FaArrowRight />
                </button>
              </div>

              {recentBlogs.length > 0 ? (
                <div className="blogs-grid">
                  {recentBlogs.map(blog => (
                    <div key={blog._id} className="blog-preview-card-wrapper">
                      <div 
                        className="blog-preview-card"
                        onClick={() => navigate(`/blogger/blogs/${blog._id}`)}
                      >
                        {blog.featuredImage && (
                          <div className="blog-preview-image">
                            <img src={blog.featuredImage} alt={blog.title} />
                            <span className={`status-badge ${blog.status}`}>
                              {blog.status}
                            </span>
                          </div>
                        )}
                        <div className="blog-preview-content">
                          <h3>{blog.title}</h3>
                          <p className="blog-preview-text">{getContentPreview(blog.content)}</p>
                          <div className="blog-meta">
                            <span className="date">📅 {formatDate(blog.createdAt)}</span>
                            <span className="engagement">
                              ❤️ {blog.likes} | 💬 {blog.comments?.length || 0} | 👁️ {blog.views || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="blog-action-buttons">
                        <button 
                          className="btn-action btn-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/blogger/blogs/edit/${blog._id}`);
                          }}
                          title="Edit Blog"
                        >
                          <FaEdit /> Edit
                        </button>
                        <button 
                          className="btn-action btn-share"
                          onClick={(e) => handleShareBlog(blog, e)}
                          title="Share Blog"
                        >
                          <FaShare /> Share
                        </button>
                        <button 
                          className="btn-action btn-delete"
                          onClick={(e) => handleDeleteBlog(blog._id, e)}
                          title="Delete Blog"
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-blogs-state">
                  <div className="empty-icon">📝</div>
                  <h3>No Blogs Yet</h3>
                  <p>Start your blogging journey by creating your first blog post!</p>
                  <button 
                    className="btn-create-first"
                    onClick={() => navigate('/blogger/blogs/create')}
                  >
                    <FaPlus /> Create Your First Blog
                  </button>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="quick-tips-section">
              <h3>Engagement Tips</h3>
              <ul className="tips-list">
                <li>Keep your content updated regularly to maintain audience interest</li>
                <li>Respond to comments to build a stronger community</li>
                <li>Use relevant tags to make your posts more discoverable</li>
                <li>Add featured images to increase engagement</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BloggerDashboard;
